import { gedaempftAuf, schriftAuf } from './kontrast'

/**
 * Die Farben der Sprechblasen — eigene und fremde.
 *
 * **Leer heißt „wie das Erscheinungsbild".** Das ist kein Sonderfall aus
 * Bequemlichkeit, sondern der einzige Weg, die dunkle Darstellung heil zu
 * lassen: eine einmal gesetzte helle Farbe gälte sonst auch dort, und eine
 * weiße Blase auf dunklem Grund wäre eine Sackgasse. Deshalb wird eine
 * Variable nur gesetzt, wenn wirklich etwas gewählt wurde.
 */
export const BLASENFARBEN = [
  {
    key: 'chat_own_color',
    variable: '--bubble-out',
    schrift: '--bubble-text-own',
    gedaempft: '--bubble-muted-own',
    /**
     * Was das Farbfeld zeigt, solange nichts gewählt ist.
     *
     * **Hier und nicht in der Karte**, weil hier schon steht, welche Blase
     * gemeint ist. In `ChatfarbenCard` stand ein einziger Wert für beide
     * Felder -- solange beide Blasen hell waren, fiel das nicht auf; mit
     * einer blauen und einer grauen zeigte es an einem der beiden Felder
     * eine Farbe, die nirgends gilt.
     */
    vorgabe: '#007aff',
  },
  {
    key: 'chat_other_color',
    variable: '--bubble-in',
    schrift: '--bubble-text-other',
    gedaempft: '--bubble-muted-other',
    vorgabe: '#e9e9eb',
  },
] as const

/** Der Name einer Blasenfarbe, wie ihn der Server kennt. */
export type Blasenfarbe = (typeof BLASENFARBEN)[number]['key']

/**
 * **Die Kette: die eigene Ansicht schlägt die der Reise** (#52).
 *
 * Aus dem Betrieb: „Auch Mitlesende sollen die Farbe der Chatblasen einstellen
 * können … Voreinstellung ist die Einstellung des Reisenden." Die Reise trägt
 * seit #49 ihre eigenen Farben (beim Anlegen aus der Voreinstellung kopiert);
 * was ein Mitlesender für sich einstellt, legt sich darüber.
 *
 * `''` heißt jeweils „eine Stufe höher", ganz oben „wie das Erscheinungsbild".
 * Als reine Funktion, damit ein Lauf sie messen kann — wie `abstandAus()` in
 * `lib/tastatur.ts`.
 */
export function blasenKette(
  meine: { own: string | null, other: string | null } | null | undefined,
  reise: { own: string | null, other: string | null } | null | undefined,
): Record<Blasenfarbe, string> {
  return {
    chat_own_color: meine?.own || reise?.own || '',
    chat_other_color: meine?.other || reise?.other || '',
  }
}

/**
 * Die Variablen für einen gewählten Satz Farben.
 *
 * Was leer bleibt, kommt **nicht** vor: der Aufrufer nimmt eine fehlende
 * Variable wieder weg, statt sie auf einen Wert zu setzen. Nur so gilt wieder,
 * was das Erscheinungsbild vorgibt.
 *
 * **Die Sprechblasen werden im dunklen Modus nicht umgefärbt** (#73), anders
 * als die Zeilen der Übersicht — und der Unterschied ist kein Versäumnis.
 *
 * Eine Zeile ist eine große Fläche auf der Seite; ein helles Rechteck in einer
 * schwarzen Liste ist eine Lampe. Eine Sprechblase ist ein kleiner Fleck, sie
 * liegt oft auf einem Foto statt auf der Seite, und ihre Schrift wird ohnehin
 * aus ihr gerechnet. Sie fällt also nicht durch — sie sieht nur genauso aus
 * wie am Tag, und das ist bei einer Farbe, die jemand für seinen Bericht
 * gewählt hat, die richtige Antwort.
 */
export function blasenVariablen(
  werte: Partial<Record<Blasenfarbe, string>>,
): Record<string, string | null> {
  const raus: Record<string, string | null> = {}
  for (const eintrag of BLASENFARBEN) {
    const farbe = werte[eintrag.key] ?? ''
    raus[eintrag.variable] = farbe === '' ? null : farbe
    raus[eintrag.schrift] = farbe === '' ? null : schriftAuf(farbe)
    raus[eintrag.gedaempft] = farbe === '' ? null : gedaempftAuf(farbe)
  }
  return raus
}
